// The Daily Cowork task text on the Authorizations page's Feed strip.
//
// SOURCE: atlas-billing-ops af96960 (main, after #18), `.claude/skills/auths-from-emr/SKILL.md`,
// sections "How to run — eCW" and "How to run — Kinnser". Each template below equals its
// section exactly, except that the section's italic first paragraph is left out because it
// is addressed to Atlas, not the reader. When a section changes, copy it again and update the
// SHA here and in AUTHS_SKILL_TEXT_SOURCE; FeedStrip.test.js pins the placeholders, not the
// prose. Same arrangement as features/queue/skillText.js.
//
// Placeholders Atlas fills: {practice_id}, {atlas_base_url}.
// The API key is never part of this text — only where it goes.

export const AUTHS_SKILL_TEXT_SOURCE = {
  repo: "atlas-billing-ops",
  path: ".claude/skills/auths-from-emr/SKILL.md",
  commit: "af96960",
  sections: { ecw: "How to run — eCW", kinnser: "How to run — Kinnser" },
};

// The EMRs the skill has a section for, in the order the dialog offers them.
export const AUTHS_SKILL_EMRS = [
  { key: "ecw", label: "eCW" },
  { key: "kinnser", label: "Kinnser" },
];

export const AUTHS_SKILL_TEXT_TEMPLATE = `**You need:** an \`auths:import\` API key for this practice in \`ATLAS_API_KEY\` (environment,
never chat or a command line), \`ATLAS_BASE_URL={atlas_base_url}\`, the atlas-billing-ops
checkout, the practice's runbook \`docs/runbooks/ecw-auths-{practice_id}.md\` (written at its
discovery session — without it, stop and ask for one), and the Chrome window Cowork works in,
logged in to eCW by a person. No script drives Chrome here: Cowork's own browser control does
step 1, and the scripts only parse and post.

**1. Export the authorization list from eCW.** Follow the runbook's "Report" section: the
menu path, the filters and the export control. Export as CSV. Change nothing in eCW. Chrome
saves the file to Downloads; move it straight into the practice's private folder:

\`\`\`bash
mkdir -p ~/.atlas-auths/{practice_id} && chmod 700 ~/.atlas-auths ~/.atlas-auths/{practice_id}
mv ~/Downloads/<the exported file>.csv ~/.atlas-auths/{practice_id}/export.csv
\`\`\`

**2. Map it to Atlas's canonical CSV.**

\`\`\`bash
cd atlas-billing-ops
python -m ops.auths.ecw_to_canonical ~/.atlas-auths/{practice_id}/export.csv --practice {practice_id}
\`\`\`

It prints \`rows N\` and the path it wrote. If it prints \`NEEDS HUMAN:\`, eCW's report layout
changed: say that line, clean up (step 5), and stop. Its messages name columns and row
numbers only.

**3. Dry run the import (always first).**

\`\`\`bash
python -m ops.auths.atlas_client --practice {practice_id} --file <the path step 2 wrote> --dry-run
\`\`\`

It prints \`rows\`, \`created\`, \`updated\`, \`unchanged\`, \`skipped\`, and each skipped row's number
with the eCW export row it came from and its reason code. Nothing is written; it leaves a
receipt beside the file.

**4. Import.**

\`\`\`bash
python -m ops.auths.atlas_client --practice {practice_id} --file <the same path> --import
\`\`\`

It refuses a file that was not dry-run (or changed since), and stops with \`NEEDS HUMAN:\` when
the dry run skipped more than half the rows — say that line and let a person decide. Only a
person adds \`--accept-skips\`.

**5. Report and clean up — every run, however it ended.** Say the counts and the reason codes.
Rows skipped as \`PATIENT_NOT_FOUND\` are counted, not listed. A person reconciling them adds
\`--list-not-found\` in their own terminal to see the eCW account numbers — never in chat, and
never look the names up. Then delete everything in the
practice's folder (the export, the canonical CSV, the receipt). If a stop came before step 1's
\`mv\`, delete the export from Downloads too.

\`\`\`bash
rm -f ~/.atlas-auths/{practice_id}/*
\`\`\`

Atlas's Authorizations page shows when authorizations last changed from the feed.

| Reason | Meaning |
|---|---|
| \`PATIENT_NOT_FOUND\` | No patient in Atlas with that account number, or that exact name and DOB. |
| \`PATIENT_AMBIGUOUS\` | More than one patient matched. |
| \`PAYER_NOT_FOUND\` | The payer name is not one of the practice's payers in Atlas (Atlas never creates one). |
| \`MISSING_DATE\` / \`BAD_DATE\` / \`BAD_DATE_RANGE\` | An effective date is blank, unreadable, or ends before it starts. |
| \`BAD_UNITS\` / \`BAD_VISITS_USED\` | Not a whole number. |
| \`BAD_LINE\` | A per-discipline cap (\`<d>_authorized\`) that is not a whole number, or a unit other than \`visits\`/\`units\` (Kinnser). |
| \`BAD_STATUS\` | A \`source_status\` other than \`active\` or \`closed\` (the mapper never writes one; a hand-edited file). |
| \`SOURCE_CLOSED\` | A \`closed\` row (cancelled or voided in the EMR) with no authorization this feed imported to retire. Nothing to do. |
| \`CLOSED_WHILE_PENDING\` | A \`closed\` row for an authorization whose request is waiting on the payer: left for a person. |
| \`SOURCE_ID_OTHER_PATIENT\` | That auth number is already on file for a different patient; Atlas will not move it. |
| \`DUPLICATE_IN_FILE\` | The same auth appears again after a row for it already landed. |
| \`PATIENT_MISMATCH\` | The account number matched a patient whose name or date of birth differs. |
| \`PATIENT_UNCONFIRMED\` | The row has an account number but no name or date of birth to confirm it. |
| \`ALREADY_ON_FILE\` | That patient already has this auth number on file (not retired), entered by hand or from another source. |
| \`INVALID\` | Atlas's authorization rules refused it (for example an NPI that is not 10 digits). |

Rows the mapper leaves out itself are listed as export row numbers with \`STATUS_SKIPPED\` (a status
the runbook says to leave out) or \`CLOSED_SUPERSEDED\` (the same authorization is also in the file as not closed —
re-entered, discharged or pending — so the cancelled row is not sent). \`SOURCE_CLOSED\` does not count toward the "skipped most rows"
stop. If the dry run says Atlas does not read \`source_status\`, stop: that Atlas would import
cancelled authorizations as active, and the import refuses the file.

A blank cell never clears anything in Atlas: an optional field left empty in eCW keeps what
Atlas has.

Exit codes: \`0\` done · \`1\` refused (Atlas said no, no key, no dry run of this file, a bad
command) · \`2\` needs a human (the mapper held the file, or the dry run skipped most rows).

**Daily, as a Cowork scheduled task.** On the biller's machine, create a scheduled task that
runs every weekday at 7:00 with this text:

> Run /auths-from-emr (eCW) for practice {practice_id}: export, map, dry run, import, report the
> counts and reason codes, delete the files. At any NEEDS HUMAN line, say it, delete the
> files, and stop.

It is a Cowork task on that machine, not a server job, because step 1 needs that person's
logged-in eCW session in their Chrome. If Chrome is closed or eCW has logged out, the run stops
at step 1 and says so.
`;

export const AUTHS_KINNSER_SKILL_TEXT_TEMPLATE = `**You need:** an \`auths:import\` API key for this practice in \`ATLAS_API_KEY\` (environment,
never chat or a command line), \`ATLAS_BASE_URL={atlas_base_url}\`, the atlas-billing-ops
checkout, the Kinnser runbook \`docs/runbooks/auths-kinnser.md\` with this practice under
\`practices\` and no \`UNKNOWN\` left in its column map (without that, stop and ask for the
discovery session), and the Chrome window Cowork works in, logged in to Kinnser by a person on
this practice's agency. No script drives Chrome here: Cowork's own browser control does step 1,
and the scripts only parse and post.

**1. Export the Authorizations report from Kinnser.** Follow the runbook's "Report" section:
Reports & Administration → Authorizations, the End Date filter and range it names, run the
report, then click **To Excel** once. Visit counts exist only in that export, never on screen.
Change nothing in Kinnser. Chrome saves the file to Downloads; move it straight into the
practice's private folder, keeping its extension:

\`\`\`bash
mkdir -p ~/.atlas-auths/{practice_id} && chmod 700 ~/.atlas-auths ~/.atlas-auths/{practice_id}
mv ~/Downloads/<the exported file> ~/.atlas-auths/{practice_id}/
\`\`\`

**2. Map it to Atlas's canonical CSV, with one line per discipline.**

\`\`\`bash
cd atlas-billing-ops
python -m ops.auths.kinnser_to_canonical ~/.atlas-auths/{practice_id}/<the exported file> --practice {practice_id}
\`\`\`

It prints \`rows N\`, \`left out N\` with each left-out export row's number and code
(\`OTHER_BRANCH\`, \`STATUS_SKIPPED\`, \`BAD_DISCIPLINE_COUNT\`), and the path it wrote. If it prints
\`NEEDS HUMAN:\`, the report or its file changed: say that line, clean up (step 5), and stop.

**3. Dry run the import (always first).**

\`\`\`bash
python -m ops.auths.atlas_client --practice {practice_id} --file <the path step 2 wrote> --dry-run
\`\`\`

It prints \`rows\`, \`created\`, \`updated\`, \`unchanged\`, \`skipped\`, and each skipped row's number
with the Kinnser export row it came from and its reason code. What lands: each discipline the
auth covers becomes its own line in Atlas (\`sn_authorized\` / \`sn_unit\`, \`pt_authorized\` / …, unit
\`visits\`); an auth's single total cap (\`authorized_units\`) is filled only when it covers exactly
one discipline. Scheduled visits are not sent — Atlas counts use from its own claims and schedule.
If the dry run instead says Atlas does not read per-discipline lines, say so in the report: that
Atlas lands single-discipline auths with their cap and auths with several disciplines with no cap.
Nothing is written; it leaves a receipt beside the file.

**4. Import.**

\`\`\`bash
python -m ops.auths.atlas_client --practice {practice_id} --file <the same path> --import
\`\`\`

The same refusals as eCW: no import without a matching dry run, and \`NEEDS HUMAN:\` when the dry
run skipped more than half the rows. Only a person adds \`--accept-skips\`.

**5. Report and clean up — every run, however it ended.** Say the counts, the left-out codes and
the reason codes. \`PATIENT_NOT_FOUND\` rows are counted, not listed. A person reconciling them
adds \`--list-not-found\` in their own terminal to see the medical record numbers (as Atlas holds
them, with the branch letter) — never in chat, and never look the names up. Then delete everything in the practice's folder, and the export from Downloads if a stop
came before step 1's \`mv\`.

\`\`\`bash
rm -f ~/.atlas-auths/{practice_id}/*
\`\`\`

The reason-code table, the blank-cell rule and the exit codes are the eCW section's.

**Daily, as a Cowork scheduled task,** the eCW text with \`(Kinnser)\` in place of \`(eCW)\`.
`;

const TEMPLATES = {
  ecw: AUTHS_SKILL_TEXT_TEMPLATE,
  kinnser: AUTHS_KINNSER_SKILL_TEXT_TEMPLATE,
};

const PLACEHOLDERS = ["practice_id", "atlas_base_url"];

// Replace every placeholder; a value we do not have becomes a visible <marker>
// rather than an empty string, so a pasted command cannot silently lose an argument.
export function fillAuthsSkillText({ practiceId, baseUrl, emr = "ecw" }) {
  const values = {
    practice_id: practiceId != null ? String(practiceId) : "<practice id>",
    atlas_base_url: baseUrl || "<atlas base url>",
  };
  return PLACEHOLDERS.reduce(
    (text, key) => text.split(`{${key}}`).join(values[key]),
    TEMPLATES[emr] || AUTHS_SKILL_TEXT_TEMPLATE,
  );
}
